import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import "dayjs/locale/es"
import { useState } from "react"
import {
  Button,
  Card,
  Col,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap"
import { Link } from "react-router-dom"
import { IEventos } from "../../interfaces/IEventos"
import { TipoEvento } from "../../interfaces/TipoEvento"
import { formatDateGuiones, hasEventPassed } from "../../utils/dateUtils"
import { getThemeByTipo } from "../../utils/tipoEventoTheme"
import "./EventosPage.css"

dayjs.locale("es")

interface EventosCardsViewProps {
  data: IEventos[]
  onAdd?: (fechaDefecto?: string) => void
  onUpdate?: (item: IEventos) => void
  onDelete?: (item: IEventos) => void
  vistaCalendario: boolean
  onToggleVista: (value: boolean) => void
}

function EventosCardsView({
  data,
  onAdd,
  onUpdate,
  onDelete,
  vistaCalendario,
  onToggleVista,
}: EventosCardsViewProps) {
  const anioActual = new Date().getFullYear()
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [nombreBuscado, setNombreBuscado] = useState<string>("")

  const filteredData = data.filter((item) => {
    const date = new Date(item.fecha)
    const day = date.getDate() + 1
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    const nombreNormalizado = nombreBuscado.trim().toLowerCase()
    const coincideNombre =
      nombreNormalizado === "" ||
      (item.nombre ?? "").toLowerCase().includes(nombreNormalizado)
    return (
      coincideNombre &&
      (selectedYear ? year === selectedYear : true) &&
      (selectedMonth ? month === selectedMonth : true) &&
      (!selectedDate ||
        (day === selectedDate.getDate() &&
          month === selectedDate.getMonth() + 1 &&
          year === selectedDate.getFullYear()))
    )
  })

  const clearFilters = () => {
    setSelectedDate(null)
    setSelectedMonth(null)
    setSelectedYear(null)
    setNombreBuscado("")
  }

  const eventosPorAnio = filteredData.reduce<Record<number, IEventos[]>>(
    (acc, item) => {
      const anio = new Date(item.fecha).getFullYear()
      if (!acc[anio]) acc[anio] = []
      acc[anio].push(item)
      return acc
    },
    {},
  )

  const aniosOrdenados = Object.keys(eventosPorAnio)
    .map((a) => Number(a))
    .sort((a, b) => b - a)

  const renderCardEvento = (item: IEventos, index: number) => (
    <Col xs={12} sm={6} md={4} lg={3} key={`${item.id}-${index}`} className="mb-3">
      <Card
        className={
          !hasEventPassed(item.fecha) ? "card-eventos" : "card-eventos-passed"
        }
        style={
          !hasEventPassed(item.fecha)
            ? {
                ["--card-bg" as string]: getThemeByTipo(item.tipo).card,
                ["--card-border" as string]: getThemeByTipo(item.tipo).border,
                ["--card-hover-bg" as string]: getThemeByTipo(item.tipo).cardHover,
              }
            : undefined
        }
      >
        <Link to={`/egresados/${item.id}`} className="link-card">
          <Card.Body className="card-body-eventos">
            <Card.Title
              className={
                !hasEventPassed(item.fecha) ? "card-title" : "card-title-passed"
              }
            >
              {item.nombre}
            </Card.Title>
            <Card.Text
              className={
                !hasEventPassed(item.fecha) ? "card-title" : "card-title-passed"
              }
            >
              {formatDateGuiones(item.fecha)}
            </Card.Text>
          </Card.Body>
        </Link>
        <div className="d-flex justify-content-center" style={{ gap: "18%" }}>
          {onUpdate && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-editar">Editar</Tooltip>}
            >
              <Button
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation()
                  onUpdate(item)
                }}
              >
                <i className="bi bi-pencil-square"></i>
              </Button>
            </OverlayTrigger>
          )}
          {onDelete && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="tooltip-eliminar">Eliminar</Tooltip>}
            >
              <Button
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(item)
                }}
              >
                <i className="bi bi-trash"></i>
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </Card>
    </Col>
  )

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col xs={12} className="text-start mb-2">
          <div className="d-flex align-items-center">
            <Button
              variant="primary"
              onClick={() => onAdd?.()}
              className="me-3 btn-agregar-eventos"
            >
              Agregar Evento <i className="bi bi-plus-square"></i>
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={vistaCalendario}
                  onChange={(e) => onToggleVista(e.target.checked)}
                  color="primary"
                />
              }
              label="Modo Calendario"
              labelPlacement="start"
            />
          </div>
          <div
            className="d-flex flex-column flex-sm-row align-items-center mt-3"
            style={{ width: "90%" }}
          >
            <TextField
              label="Buscar por nombre de evento"
              value={nombreBuscado}
              onChange={(e) => setNombreBuscado(e.target.value)}
              className="w-100 w-sm-auto"
              sx={{ maxWidth: 500, width: { sm: 500 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <i
                      className="bi bi-search"
                      style={{ pointerEvents: "none", color: "inherit" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Seleccionar Fecha"
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(newValue) =>
                  setSelectedDate(newValue ? newValue.toDate() : null)
                }
                className="ms-4 w-100 w-sm-auto date-picker"
                format="DD/MM/YYYY"
                sx={{ maxWidth: 250 }}
              />
              <DatePicker
                label="Seleccionar Mes"
                value={selectedMonth ? dayjs().month(selectedMonth - 1) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedMonth(newValue.month() + 1)
                  } else {
                    setSelectedMonth(null)
                  }
                }}
                views={["month"]}
                openTo="month"
                className="ms-4 w-100 w-sm-auto"
                sx={{ maxWidth: 250 }}
              />
              <DatePicker
                label="Seleccionar Año"
                value={selectedYear ? dayjs(`${selectedYear}-01-01`) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedYear(newValue.year())
                  } else {
                    setSelectedYear(null)
                  }
                }}
                views={["year"]}
                openTo="year"
                className="ms-4 w-100 w-sm-auto"
                sx={{ maxWidth: 250 }}
              />
            </LocalizationProvider>
            <Button
              variant="secondary"
              className="ms-4 btn-limpiar mb-1 w-sm-auto"
              onClick={clearFilters}
            >
              Limpiar
            </Button>
          </div>
        </Col>
      </Row>

      {aniosOrdenados.map((anio) => (
        <Accordion
          key={anio}
          defaultExpanded={anio === anioActual}
          disableGutters
          elevation={0}
          sx={{
            mb: 1,
            background: "transparent",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              border: "1px solid #d0d0d0",
              borderRadius: 1,
              minHeight: 24,
              "&.Mui-expanded": { minHeight: 24 },
              "& .MuiAccordionSummary-content": {
                my: 0.25,
                "&.Mui-expanded": { my: 0.25 },
              },
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", mr: 1 }}>
              {anio}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
              · {eventosPorAnio[anio].length}{" "}
              {eventosPorAnio[anio].length === 1 ? "evento" : "eventos"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, pb: 0 }}>
            <Row>
              {eventosPorAnio[anio].map((item, index) =>
                renderCardEvento(item, index),
              )}
            </Row>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  )
}

export default EventosCardsView
